/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/failedPayments.scss';

import {AwesomeIcon, ChangeFunction, copyToClipboard, Loader, SelectItemShape} from '@dvrd/dvr-controls';
import React, {KeyboardEventHandler, MouseEventHandler} from 'react';
import {PaymentFailureStatus} from "../../../util/interfaces";
import FailedPayment from "../../../models/failedPaymentModel";
import {parseMoney} from "../../../util/utils";
import {BolmateInput, BolmateSelect} from "../../controls";
import classNames from 'classnames';
import {onShowConfirmSnackbar} from "../../../util/renderUtil";

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onChangeStatus: (status: PaymentFailureStatus) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onChangePage: (nextPage: number) => MouseEventHandler;
    onGenerateInvoice: (payment: FailedPayment, sendImmediately: boolean) => MouseEventHandler;
    onSendInvoice: (payment: FailedPayment) => MouseEventHandler;
    onDeletePayment: (payment: FailedPayment) => MouseEventHandler;
    onEnterSearch: KeyboardEventHandler;
    payments: Array<FailedPayment>;
    loading: boolean;
    status: PaymentFailureStatus | null;
    search: string;
    limit: number;
    page: number;
    total: number;
}

const limitItems: SelectItemShape[] = [
    {label: '10', value: 10},
    {label: '20', value: 20},
    {label: '50', value: 50},
];

export default function FailedPayments(props: Props) {
    const {
        loading, payments, page, onChangeLimit, limit, onChangeSearch, search, onChangePage, onChangeStatus,
        status, onGenerateInvoice, total, onSendInvoice, onDeletePayment, onEnterSearch
    } = props;

    function onCopy(value?: string) {
        return function () {
            if (!value) return;
            copyToClipboard(value).then(() => {
                onShowConfirmSnackbar(`'${value}' gekopieerd naar klembord`);
            });
        }
    }

    function renderHeader() {
        return (
            <div className='header-container'>
                <h3>Mislukte betalingen</h3>
                <BolmateInput onChange={onChangeSearch} value={search} label='Zoeken'
                              inputProps={{placeholder: 'Zoek een betaling'}} disabled={loading}
                              onEnter={onEnterSearch}/>
                <div className='status-buttons'>
                    {renderStatusButton(PaymentFailureStatus.NEW)}
                    {renderStatusButton(PaymentFailureStatus.GENERATED)}
                    {renderStatusButton(PaymentFailureStatus.SENT)}
                    {renderStatusButton(PaymentFailureStatus.CLOSED)}
                </div>
            </div>
        )
    }

    function renderStatusButton(_status: PaymentFailureStatus) {
        const active = status === _status;
        const label = {
            [PaymentFailureStatus.NEW]: 'Nieuw',
            [PaymentFailureStatus.GENERATED]: 'Factuur aangemaakt',
            [PaymentFailureStatus.SENT]: 'Factuur verzonden',
            [PaymentFailureStatus.CLOSED]: 'Factuur betaald'
        }[_status];
        return (
            <div className={classNames('status-button', loading && 'disabled', active && 'active')}
                 onClick={onChangeStatus(_status)}>
                <label className='button-label'>{label}</label>
            </div>
        )
    }

    function renderPayments() {
        return (
            <div className='body-container'>
                <div className='payments-container'>
                    <div className='row default-table-row head'>
                        <label>DATUM MISLUKT</label>
                        <label>STATUS</label>
                        <label>GEBRUIKER ID</label>
                        <label>GEBRUIKER</label>
                        <label>STRIPE KLANT ID</label>
                        <label>BEDRAG EXCL.</label>
                        <label/>
                    </div>
                    {payments.map(renderPayment)}
                    {!total && (
                        <div className='default-table-row empty'>
                            <label className='empty-label'>- Geen betalingen gevonden -</label>
                        </div>
                    )}
                </div>
                {renderPagination()}
            </div>
        )
    }

    function renderPayment(payment: FailedPayment, index: number) {
        return (
            <div key={index} className='row default-table-row'>
                <label>{payment.createdAtMoment.format('DD-MM-YYYY')}</label>
                <label>{payment.translatedStatus}</label>
                <label onClick={onCopy(payment.userId)} className='click'>{payment.userId}</label>
                <label>{payment.user?.fullName ?? '-'}</label>
                <label className='click' onClick={onCopy(payment.user?.subscription?.customerId)}>
                    {payment.user?.subscription?.customerId}{payment.hasOther &&
                    <AwesomeIcon name='exclamation-triangle' className='other-icon'
                                 title='Heeft al openstaande facturen'/>}</label>
                <label>&euro;{parseMoney(payment.amountExcl)}</label>
                {renderPaymentAction(payment)}
            </div>
        )
    }

    function renderPaymentAction(payment: FailedPayment) {
        const {status} = payment;
        if (status === PaymentFailureStatus.NEW) return renderNewActions(payment);
        else if (status === PaymentFailureStatus.CLOSED) return renderClosedActions(payment);
        else return renderGeneratedActions(payment);
    }

    function renderNewActions(payment: FailedPayment) {
        return (
            <div className='payment-actions new'>
                <div className='action green' onClick={onGenerateInvoice(payment, true)}>
                    <AwesomeIcon name='check' className='action-icon'/>
                    <span className='tooltip'>Nieuwe factuur verzenden</span>
                </div>
                <div className='action blue' onClick={onGenerateInvoice(payment, false)}>
                    <AwesomeIcon name='file-invoice' className='action-icon'/>
                    <span className='tooltip left'>Nieuwe factuur aanmaken (niet verzenden)</span>
                </div>
                <div className='action delete' onClick={onDeletePayment(payment)}>
                    <AwesomeIcon name='times' className='action-icon'/>
                    <span className='tooltip'>Verwijderen</span>
                </div>
            </div>
        );
    }

    function renderGeneratedActions(payment: FailedPayment) {
        return (
            <div className='payment-actions generated'>
                <div className='action blue' onClick={onSendInvoice(payment)}>
                    <AwesomeIcon name='paper-plane' className='action-icon'/>
                    <span className='tooltip'>Factuur verzenden</span>
                </div>
                <div className={classNames('action green', !payment.paymentUrl && 'disabled')}
                     onClick={onCopy(payment.paymentUrl)}>
                    <AwesomeIcon name='link' className='action-icon'/>
                    <span className='tooltip'>Betaallink kopiëren</span>
                </div>
                <div className='action delete' onClick={onDeletePayment(payment)}>
                    <AwesomeIcon name='times' className='action-icon'/>
                    <span className='tooltip'>Verwijderen</span>
                </div>
            </div>
        )
    }

    function renderClosedActions(payment: FailedPayment) {
        return (
            <div className='payment-actions closed'>
                <div className='action green nb'>
                    <AwesomeIcon name='check' className='icon-paid'/>
                    <span className='tooltip'>Factuur betaald</span>
                </div>
                <div className='action delete' onClick={onDeletePayment(payment)}>
                    <AwesomeIcon name='times' className='action-icon'/>
                    <span className='tooltip'>Verwijderen</span>
                </div>
            </div>
        )
    }

    function renderPagination() {
        const from = Math.min(total, page * limit + 1);
        const to = Math.min(total, (page + 1) * limit);
        const maxPage = Math.ceil(total / limit) - 1;
        const prevDisabled = page === 0;
        const nextDisabled = page >= maxPage;
        return (
            <div className='pagination-container'>
                <label>{from} t/m {to} van {total}</label>
                <AwesomeIcon name='chevron-left' onClick={prevDisabled ? undefined : onChangePage(page - 1)}
                             className={classNames('pager', prevDisabled && 'disabled')}/>
                <AwesomeIcon name='chevron-right' onClick={nextDisabled ? undefined : onChangePage(page + 1)}
                             className={classNames('pager', nextDisabled && 'disabled')}/>
                <BolmateSelect onChange={onChangeLimit} value={limit} items={limitItems} disabled={loading}
                               itemsPosition='top'/>

            </div>
        )
    }

    return (
        <div className='admin-failed-payments'>
            <Loader active={loading}/>
            {renderHeader()}
            {renderPayments()}
        </div>
    )
}